import { T, useGT, Var } from 'gt-react';
import { useQuery } from '@tanstack/react-query';
import { Action, Subject } from '@erp/authz';
import { SYSTEM_ENTITY_LABELS } from '@erp/shared-types';
import { orpc } from 'src/api/rpc/client';
import { useCan } from 'src/hooks/useCan';
import { Label } from 'src/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';
import {
  nextConfigForTaskTypeSelection,
  selectedTaskTypeForStepConfig,
} from './taskTypeStepConfig';
import {
  taskTypeInputSchemaHint,
  taskTypeKeyStatusHelp,
  type TaskTypeInputSchemaHint,
  type TaskTypeKeyStatusHelp,
} from './taskTypeKeyFieldModel';

interface TaskTypeKeyConfigFieldProps {
  config: Record<string, unknown>;
  /** Same contract as ConfigRendererProps.onConfigChange (may be async). */
  onConfigChange: (
    newConfig: Record<string, unknown>,
  ) => void | Promise<unknown>;
  /** Marks the type as required for this step (resolve_task needs one). */
  required?: boolean;
  /** The task type cannot be supplied by an input socket. */
  locksTaskType?: boolean;
  /**
   * The selected type's input-schema fields become sockets on this step.
   * Create Task leaves the type overridable, so it does not.
   */
  enforcesInputSockets?: boolean;
  /** Hide the input-schema note. Resolve Task does not create with that schema. */
  showInputSchemaHint?: boolean;
  /** When true, the select must not mutate workflow configuration. */
  readOnly?: boolean;
}

/** Sentinel select value for "no task type" (an untyped task). */
const NONE_VALUE = '__none__';

function TaskTypeNoneOption({ required }: { required: boolean }) {
  if (required) {
    return (
      <T context="task type not selected yet (required field)">
        Select a task type...
      </T>
    );
  }
  return <T context="task type none option (untyped task)">No type</T>;
}

function TaskTypeStatusHelp({ kind }: { kind: TaskTypeKeyStatusHelp }) {
  if (kind === 'unavailable') {
    return (
      <T context="task types cannot be loaded">
        Task types are unavailable right now. The saved selection is kept.
      </T>
    );
  }
  if (kind === 'loading') {
    return <T context="loading task types">Loading task types...</T>;
  }
  if (kind === 'locked') {
    return (
      <T context="help text for a workflow node whose task type is fixed in configuration">
        This node always creates tasks of the selected type. The type cannot be
        overridden by an input.
      </T>
    );
  }
  if (kind === 'required') {
    return (
      <T context="help text for required workflow task type field">
        The org task type resolved by this step. Can also be wired from an
        upstream step via the taskTypeKey input.
      </T>
    );
  }
  return (
    <T context="help text for optional workflow task type field">
      Typed tasks gain the type's behavior: anchoring, outcome vocabulary, and
      completion gating.
    </T>
  );
}

function TaskTypeInputSchemaNote({ hint }: { hint: TaskTypeInputSchemaHint }) {
  if (hint === 'enforced') {
    return (
      <T context="workflow task node, type requires typed input sockets">
        This type's input fields are sockets on this step. Required fields must
        be wired before the workflow can run.
      </T>
    );
  }
  return (
    <T context="workflow create task node, type has an input schema this node does not enforce as sockets">
      This type requires those fields when the task is created. This node does
      not add them as sockets, because the task type can still be chosen at run
      time. Use Create Typed Task to require each field on the canvas.
    </T>
  );
}

/**
 * Config field for choosing the org task type a task step creates/resolves
 * tasks as. Stores the type's stable `key` in config as `taskTypeKey`
 * (portable across orgs; handlers resolve it to the org's type at run time).
 * Selecting "No type" removes the key so the task is created untyped.
 */
export function TaskTypeKeyConfigField({
  config,
  onConfigChange,
  required = false,
  locksTaskType = false,
  enforcesInputSockets = false,
  showInputSchemaHint = true,
  readOnly = false,
}: TaskTypeKeyConfigFieldProps) {
  const gt = useGT();
  const [canReadTasks] = useCan(Action.Read, Subject.Task);
  const {
    data: taskTypes,
    isLoading,
    isError,
  } = useQuery({
    ...orpc.taskTypes.list.queryOptions({ input: {} }),
    enabled: canReadTasks,
    staleTime: 5 * 60 * 1000,
  });

  const selectedKey =
    typeof config.taskTypeKey === 'string' ? config.taskTypeKey : NONE_VALUE;
  const taskTypeOptions = taskTypes ?? [];
  // Read-only when the list can't be loaded: an editable control showing only
  // "No type" would silently wipe a saved key on selection.
  const taskTypesUnavailable = !canReadTasks || isError;
  const selectedKeyInOptions =
    selectedKey === NONE_VALUE ||
    taskTypeOptions.some((taskType) => taskType.key === selectedKey);

  const selectedType = taskTypeOptions.find(
    (taskType) => taskType.key === selectedKey,
  );
  const requiredAnchorLabel = selectedType?.anchorEntityType
    ? (SYSTEM_ENTITY_LABELS[selectedType.anchorEntityType] ??
      selectedType.anchorEntityType)
    : null;
  const statusHelp = taskTypeKeyStatusHelp({
    taskTypesUnavailable,
    isLoading,
    locksTaskType,
    required,
  });
  const schemaHint = taskTypeInputSchemaHint({
    showInputSchemaHint,
    fieldCount: selectedType?.inputSchema?.fields.length ?? 0,
    enforcesInputSockets,
  });

  function handleChange(nextValue: string) {
    if (nextValue === NONE_VALUE) {
      void onConfigChange(nextConfigForTaskTypeSelection(config, null));
      return;
    }
    const nextType = taskTypeOptions.find(
      (taskType) => taskType.key === nextValue,
    );
    void onConfigChange(
      nextConfigForTaskTypeSelection(
        config,
        nextType ? selectedTaskTypeForStepConfig(nextType) : { key: nextValue },
      ),
    );
  }

  return (
    <div className="space-y-2">
      <Label htmlFor="taskTypeKey" className="text-sm font-medium">
        <T context="workflow task node, task type field label">Task Type</T>
      </Label>
      <Select
        value={selectedKey}
        onValueChange={handleChange}
        disabled={taskTypesUnavailable || isLoading || readOnly}
      >
        <SelectTrigger id="taskTypeKey" className="w-full">
          <SelectValue
            placeholder={gt('No type', {
              context: 'placeholder for untyped task type select',
            })}
          />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE_VALUE}>
            <TaskTypeNoneOption required={required} />
          </SelectItem>
          {/* Keep a saved key visible (and its value intact) even when it is
              not in the loaded list — e.g. list unavailable or key archived. */}
          {!selectedKeyInOptions && (
            <SelectItem value={selectedKey}>{selectedKey}</SelectItem>
          )}
          {taskTypeOptions.map((taskType) => (
            <SelectItem key={taskType.id} value={taskType.key}>
              {taskType.name} ({taskType.key})
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="text-xs text-muted-foreground">
        <TaskTypeStatusHelp kind={statusHelp} />
      </p>
      {requiredAnchorLabel ? (
        <p className="text-xs text-muted-foreground">
          <T context="workflow task node, type requires a matching source entity socket">
            This type requires a <Var>{requiredAnchorLabel}</Var>. That entity
            input is now required and only accepts that type.
          </T>
        </p>
      ) : null}
      {schemaHint ? (
        <p className="text-xs text-muted-foreground">
          <TaskTypeInputSchemaNote hint={schemaHint} />
        </p>
      ) : null}
    </div>
  );
}
